import { Component, Input, Output, OnChanges, SimpleChanges, ChangeDetectorRef, EventEmitter, ViewEncapsulation } from '@angular/core';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { BcAnalytics } from 'common/analytics/analytics.service';
import * as moment from 'moment';
import { MessageGroupService } from '../../services/message-group/message-group.service';
import { VisitorsService } from 'common/visitors/visitors.service';

@Component({
  selector: 'app-visitor-history',
  templateUrl: './visitor-history.component.html',
  styleUrls: ['./visitor-history.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class VisitorHistoryComponent implements OnChanges {
  @Input()
  public chatId;
  @Input()
  public textChatId;
  @Input()
  public emailId;
  @Input()
  public emailThreadId;
  @Input()
  public relation;
  @Input()
  public showOldestFirst: boolean;
  @Input()
  public tabName: string = '';
  @Output()
  public loaded = new EventEmitter<void>();

  public moment;
  public historyItems = [];
  public historyItemMap = new Map();
  public historyLoaded: boolean = false;
  public dataLoaded: boolean = false;

  constructor(private _bcVisitorsService: VisitorsService, private _bcAnalytics: BcAnalytics,
              private _messageGroupService: MessageGroupService,
              private _changeDetector: ChangeDetectorRef) {
    this.moment = moment;
  }

  ngOnChanges(changes: SimpleChanges) {
    if ((changes.relation && this.historyLoaded) || changes.chatId || changes.emailThreadId || changes.textChatId) {
      this.dataLoaded = false;
      this.historyItems = [];
      this.fetchHistory();
    }
  }

  fetchHistory() {
    if (this.chatId) {
      this.initChatHistoryItems(this.chatId);
    }

    if (this.emailThreadId) {
      this.initEmailHistoryItems(this.emailThreadId, this.emailId);
    }

    if (this.textChatId) {
      this.initTextChatHistoryItems(this.textChatId);
    }
  }

  initEmailHistoryItems(emailThreadId, emailId) {
    return this._bcVisitorsService.getVisitorsEmailThreadHistory(emailThreadId).then(items => {
      const filteredItems = items.filter(item => {
        return !this.isItemEmail(item) || item.id !== emailId;
      });
      this._setHistoryItems(filteredItems);
    });
  }

  initChatHistoryItems(chatId) {
    return this._bcVisitorsService.getVisitorHistoryByChatId(chatId).then(items => {
      this._setHistoryItems(items);
    });
  }

  initTextChatHistoryItems(chatId) {
    return this._bcVisitorsService.getVisitorHistoryByTextChatId(chatId).then(items => {
      this._setHistoryItems(items);
    });
  }

  private _setHistoryItems(items) {
    let sortedItems = items;
    if (this.showOldestFirst) {
      sortedItems = items.sort((a, b) => {
        const firstTime = moment(a.created).unix();
        const secondTime = moment(b.created).unix();
        if (firstTime > secondTime) {
          return 1;
        } else if (secondTime > firstTime) {
          return -1;
        }
        return 0;
      });
    }
    this.historyItems = sortedItems;
    this._bcAnalytics.eventTrack('VisitorHistoryItemCount', { count: this.historyItems.length });
    this.initHistoryItemMap();
    this.historyLoaded = true;
    this.dataLoaded = true;
    if (!this._changeDetector['destroyed']) { // eslint-disable-line @typescript-eslint/dot-notation
      this._changeDetector.detectChanges();
    }
    this.loaded.emit();
  }

  initHistoryItemMap() {
    const iterableArray: Iterable<any> = this.historyItems.map(item => [item.id, item]);
    this.historyItemMap = new Map(iterableArray);
  }

  isItemEmail(item) {
    return item.type === WorkItemTypes.EMAIL;
  }

  isItemChat(item) {
    return item.type === WorkItemTypes.CHAT;
  }

  public isItemTextChat(item): boolean {
    return this._isItemSms(item) || this._isItemFacebook(item) || this._isItemAsyncMessage(item);
  }

  private _isItemSms(item): boolean {
    return item.type === WorkItemTypes.SMS;
  }

  private _isItemFacebook(item): boolean {
    return item.type === WorkItemTypes.FACEBOOK;
  }

  private _isItemAsyncMessage(item): boolean {
    return item.type === WorkItemTypes.ASYNC_MESSAGE;
  }

  calculateHistoryItemIcon(historyItem) {
    let iconToUse = '';
    if (historyItem.loading) {
      return 'refresh';
    }
    if (this.isItemChat(historyItem)) {
      iconToUse = this.getEventBasedChatIcon(historyItem.additionalEvents);
    }
    if (this.isItemEmail(historyItem)) {
      iconToUse = 'mail';
    }
    if (this._isItemFacebook(historyItem)) {
      iconToUse = 'message';
    }
    if (this._isItemAsyncMessage(historyItem)) {
      iconToUse = 'async-message';
    }
    if (this._isItemSms(historyItem)) {
      iconToUse = 'sms';
    }
    return iconToUse;
  }

  getEventBasedChatIcon(events) {
    let chatIcon = 'chat-large';
    if (events && events.includes('remote-control')) {
      chatIcon = 'chat-remote-access';
    }
    return chatIcon;
  }

  getHistoryItemClasses(itemId) {
    const item = this.historyItemMap.get(itemId);
    let classes = '';

    classes += item.isDetailsOpen ? 'open' : 'closed';

    if (item.type === WorkItemTypes.CHAT) {
      classes += ' chat-history-item';
    } else if (item.type === WorkItemTypes.EMAIL) {
      classes += ' email-history-item';
    } else if (item.type === WorkItemTypes.FACEBOOK) {
      classes += ' facebook-history-item';
    } else if (item.type === WorkItemTypes.ASYNC_MESSAGE) {
      classes += ' async-message-history-item';
    } else if (item.type === WorkItemTypes.SMS) {
      classes += ' sms-history-item';
    }
    return classes;
  }

  downloadDetailsIfNeeded(item) {
    if (item.loading) {
      return;
    }

    if (!item.details) {
      if (item.type === WorkItemTypes.CHAT) {
        this.downloadChatDetails(item);
      } else if (item.type === WorkItemTypes.EMAIL) {
        this.downloadEmailDetails(item);
      } else if ([WorkItemTypes.SMS, WorkItemTypes.FACEBOOK, WorkItemTypes.ASYNC_MESSAGE].includes(item.type)) {
        this.downloadTextChatDetails(item);
      }
    } else if (item.type === WorkItemTypes.EMAIL) {
      item.loading = true;
    }
  }

  downloadChatDetails(item) {
    item.loading = this._bcVisitorsService.getVisitorHistoryChatDetails(item.id).then(details => {
      item.details = this._messageGroupService.groupMessagesByTypeAndUser(details, item.type);
      item.loading = null;
      this._changeDetector.detectChanges();
    });
  }

  downloadEmailDetails(item) {
    item.loading = this._bcVisitorsService.getVisitorHistoryEmailDetails(item.id).then(details => {
      item.details = details;
      this._changeDetector.detectChanges();
    });
  }

  downloadTextChatDetails(item) {
    item.loading = this._bcVisitorsService.getVisitorHistoryTextChatDetails(item.id).then(details => {
      item.details = this._messageGroupService.groupMessagesByType(details, item.type);
      item.loading = null;
      this._changeDetector.detectChanges();
    });
  }

  onEmailBodyFrameLoaded(item) {
    item.loading = null;
    this._changeDetector.detectChanges();
  }

  flipDetailsState(itemId) {
    const item = this.historyItemMap.get(itemId);

    item.isDetailsOpen = !item.isDetailsOpen;

    if (item.isDetailsOpen) {
      this.downloadDetailsIfNeeded(item);
    }

    this._bcAnalytics.eventTrack('OpenVisitorHistoryItem', {
      itemType: item.type,
      to: item.isDetailsOpen ? 'open' : 'closed',
      at: this.tabName || 'Conversation'
    });
  }
}
